import {useCallback, useEffect, useRef} from "react";

// iOS Safari does not bring a focused field into view when the page scrolls
// in an inner overflow container: the app shell locks the document with
// overflow-hidden, and Safari's automatic reveal-on-focus only pans the
// document scroller. On the first tap the keyboard and the AutoFill bar
// open right on top of the login field and nothing moves. (Chrome on iOS
// scrolls nested containers itself, so it never shows the bug.)
//
// While a field is focused and the keyboard covers part of the layout
// viewport (the visual viewport shrinks; the layout viewport does not),
// the spacer rendered after the form is sized to the covered height so the
// inner scroller gains scroll range, and the field is scrolled into the
// strip that stays visible above the keyboard.
//
// Ignore shrinks smaller than this: URL-bar show/hide jitter, not a
// keyboard. Also keeps the hook inert on desktop.
const KEYBOARD_MIN_PX = 80;

// How long the spacer is frozen after a blur. A tap on Continue blurs the
// field first, and Safari starts closing the keyboard on that blur, so its
// visual-viewport resize can fire BEFORE the click lands. Resizing the
// spacer then clamps the scroller and slides the button out from under the
// finger, which is the layout-shift-under-tap this hook exists to avoid.
// Comfortably longer than the ~250ms iOS dismiss animation.
const BLUR_SETTLE_MS = 400;

const coveredByKeyboard = () => {
  const vv = window.visualViewport;
  return vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;
};

// iOS Safari (bottom-tab-bar layout) floats its collapsed URL pill OVER the
// page while the keyboard is up, and visualViewport.height does NOT exclude
// it -- so content aligned to the visual-viewport bottom sits under the pill
// (QA screenshot 2026-07-29: Continue half-hidden behind the
// "idaho-et-preview.pages.dev" pill). No API measures that chrome, so the
// reveal aims this much above the reported bottom. Gated to Safari-on-iOS:
// iOS Chrome/Firefox/Edge and Android draw no floating chrome there, and on
// Safari's top-address-bar layout the only cost is the form sitting a bit
// higher (the field-top clamp still rules).
const IOS_SAFARI_CHROME_PX = 90;
const isIosSafari = () => {
  const ua = navigator.userAgent || "";
  return /iP(hone|od|ad)/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
};

const useKeyboardReveal = () => {
  const fieldRef = useRef(null);
  const spacerRef = useRef(null);
  // Attach to the last element that must stay visible above the keyboard
  // (the Continue button). Falls back to the field itself if unset.
  const actionRef = useRef(null);
  // Set for BLUR_SETTLE_MS after a blur; see BLUR_SETTLE_MS.
  const blurHoldRef = useRef(false);
  const blurTimerRef = useRef(null);

  const sync = useCallback(() => {
    const el = fieldRef.current;
    const spacer = spacerRef.current;
    if (!spacer) return;
    // Frozen: leave the spacer at whatever height the tap saw.
    if (blurHoldRef.current) return;
    // Measured, never inferred from focus: a blurred field with the keyboard
    // still up must keep its spacer, and the keyboard is only gone when the
    // visual viewport says so.
    const kb = coveredByKeyboard();
    // The height is written imperatively, not through state: the write and
    // the scroll math below must land in one synchronous layout pass so the
    // scroller already has its new range when we scroll. A state-driven
    // spacer commits after this handler runs, which is too late; the scroll
    // clamps after a few pixels. (The host must not pass a style prop on
    // the spacer div, or re-renders would wipe this height.)
    spacer.style.height = kb >= KEYBOARD_MIN_PX ? `${kb}px` : "0px";
    if (!el || kb < KEYBOARD_MIN_PX) return;
    // Nearest ancestor that is a real scroll container with room to
    // scroll. Checking only scrollHeight > clientHeight is wrong here: the
    // login card is h-full with visible overflow, so it reports overflow
    // too, and scrollBy on a non-scroll-container is a silent no-op.
    let scroller = el.parentElement;
    while (scroller) {
      const oy = getComputedStyle(scroller).overflowY;
      if (
        (oy === "auto" || oy === "scroll") &&
        scroller.scrollHeight > scroller.clientHeight + 1
      )
        break;
      scroller = scroller.parentElement;
    }
    if (!scroller) return;
    // Undo Safari's document force-pan. With the keyboard up, Safari
    // sometimes scrolls the DOCUMENT to reveal the caret even though the
    // shell locks it (scrollHeight == innerHeight, so any scrollY is
    // Safari's own overpan, not real scroll range). That pan shoves the
    // field off the top and shows blank out-of-document space above the
    // keyboard. Reset it and let the inner-scroller math below do the
    // reveal. Guarded so a document that ever becomes genuinely scrollable
    // is left alone.
    if (
      window.scrollY > 0 &&
      document.documentElement.scrollHeight <= window.innerHeight + 1
    ) {
      window.scrollTo(0, 0);
    }
    // Scroll the MINIMUM that lifts the action area (Continue) above the
    // keyboard: minimal scroll keeps the most content above the field on
    // screen (the Phone/Email toggle). Clamped so the field itself never
    // leaves the top. Instant scroll on purpose: Safari can cancel smooth
    // scrolls while the keyboard animation runs.
    const vv = window.visualViewport;
    const stripTop = vv.offsetTop;
    const stripBottom = vv.offsetTop + vv.height;
    const bottomEl = actionRef.current || el;
    const chromePad = isIosSafari() ? IOS_SAFARI_CHROME_PX : 0;
    const below =
      bottomEl.getBoundingClientRect().bottom + 16 + chromePad - stripBottom;
    // How far the field's top sits below the strip's top. Negative = the
    // field has been pushed above the visible strip (our scroll and the
    // browser's own reveal both fired, or an autofill bar grew then shrank).
    const fieldTopGap = el.getBoundingClientRect().top - stripTop - 12;
    let delta = 0;
    if (below > 4) {
      // Lift Continue above the keyboard, but never past the field's top:
      // when both can't fit, the field the user is typing in wins.
      delta = Math.min(below, fieldTopGap);
    } else if (fieldTopGap < -4) {
      // Convergence, not a one-shot: scroll back UP when the field is above
      // the strip. Before this branch the hook could only ever scroll down,
      // so any overshoot became permanent -- the toggle icons and the field
      // stayed hidden until blur.
      delta = fieldTopGap;
    }
    if (Math.abs(delta) > 4) scroller.scrollBy(0, delta);
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    vv.addEventListener("resize", sync);
    // Safari's keyboard pan moves the visual viewport WITHOUT resizing it
    // (offsetTop changes, height doesn't), which fires only "scroll". A
    // resize-only listener never re-evaluates after that pan, so it can't
    // correct the overshoot it causes.
    vv.addEventListener("scroll", sync);
    return () => {
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
      clearTimeout(blurTimerRef.current);
    };
  }, [sync]);

  const fieldProps = {
    onFocus: (e) => {
      fieldRef.current = e.target;
      // Focus wins over a pending blur hold: this is a field-to-field move
      // (or a refocus), so the layout should follow the new field now.
      clearTimeout(blurTimerRef.current);
      blurHoldRef.current = false;
      // Keyboard opening fires visual-viewport resizes, which re-sync with
      // final geometry; this direct call covers field-to-field focus where
      // the keyboard is already up and no resize will fire.
      sync();
    },
    onBlur: () => {
      // Drop the field but keep the spacer until the keyboard actually
      // closes. Removing it at blur -- or letting the resize that the blur
      // itself triggers remove it -- shifts the layout under a Continue tap
      // before its click lands, so freeze first and settle after.
      fieldRef.current = null;
      blurHoldRef.current = true;
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = setTimeout(() => {
        blurHoldRef.current = false;
        sync();
      }, BLUR_SETTLE_MS);
    },
  };

  return {fieldProps, spacerRef, actionRef};
};

export default useKeyboardReveal;
